import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, Input, OnDestroy, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import type { Iti } from 'intl-tel-input';

@Component({
  selector: 'app-custom-tel-input',
  templateUrl: './custom-tel-input.component.html',
  styleUrls: ['./custom-tel-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomTelInputComponent implements AfterViewInit, OnDestroy {
  @ViewChild('phoneInput') phoneInput!: ElementRef<HTMLInputElement>;
  @ViewChild('phoneWrapper', { static: true }) phoneWrapper!: ElementRef<HTMLElement>;

  @Input() phoneNumberControl!: FormControl;
  @Input() label = '';

  private iti!: Iti;

  readonly preferredCountries = ['ch', 'de', 'fr', 'at', 'it', 'gb'] as any;

  constructor(
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.iti = intlTelInput(this.phoneInput.nativeElement, {
      initialCountry: 'ch',
      countryOrder: this.preferredCountries,
      separateDialCode: false,
      formatAsYouType: true,
      countrySelectorMode: 'DROPDOWN',
      countrySearch: true,
      matchDropdownWidth: true,
      strictRejectAnimation: false,
      dropdownParent: document.body,
      loadUtils: () => import('intl-tel-input/utils')
    });

    // Add phone format validator — only fires when a value is present
    const phoneValidator: ValidatorFn = (): ValidationErrors | null => {
      if (!this.phoneInput?.nativeElement?.value?.trim()) {
        return null; // let Validators.required handle the empty case
      }
      return this.iti.isValidNumber() ? null : { invalidPhoneNumber: true };
    };
    this.phoneNumberControl.addValidators(phoneValidator);
    this.phoneNumberControl.updateValueAndValidity({ emitEvent: false });

    const countryButton = this.phoneWrapper.nativeElement.querySelector(
      '.iti__selected-country'
    ) as HTMLButtonElement;

    if (countryButton) {
      countryButton.tabIndex = -1;
    }

    this.updateCountryVisibility();

    this.phoneInput.nativeElement.addEventListener('focus', () => {
      this.updateCountryVisibility();

      this.updateDropdownWidth();
    });

    this.phoneInput.nativeElement.addEventListener('blur', () => {
      // Format the number on blur if valid
      const intlNumber = this.iti.getNumber();
      if (intlNumber && this.iti.isValidNumber()) {
        this.iti.setNumber(intlNumber);
      }

      this.phoneNumberControl.setValue(this.phoneInput.nativeElement.value, { emitEvent: false });
      this.phoneNumberControl.markAsTouched();
      this.phoneNumberControl.updateValueAndValidity();
      this.cdr.markForCheck();

      setTimeout(() => this.updateCountryVisibility(), 100);
    });

    this.phoneNumberControl.valueChanges.subscribe(() => {
      this.updateCountryVisibility();
    });

    if (this.phoneNumberControl.value) {
      this.iti.setNumber(this.phoneNumberControl.value);
    }

    this.phoneInput.nativeElement.addEventListener('input', () => {
      const input = this.phoneInput.nativeElement;
      const value = input.value.trim();

      // Reset when input is cleared
      if (!value) {
        this.phoneNumberControl.setValue('', { emitEvent: false });
        this.phoneNumberControl.markAsDirty();
        this.phoneNumberControl.updateValueAndValidity();
        this.cdr.markForCheck();
        return;
      }

      // Sync form control with formatted display value
      this.phoneNumberControl.setValue(value, { emitEvent: false });
      this.phoneNumberControl.markAsDirty();
      this.phoneNumberControl.updateValueAndValidity();
      this.cdr.markForCheck();
    });

    this.phoneInput.nativeElement.addEventListener('countrychange', () => {
      this.phoneNumberControl.setValue(
        this.phoneInput.nativeElement.value,
        { emitEvent: false }
      );
      this.phoneNumberControl.markAsDirty();
      this.phoneNumberControl.updateValueAndValidity();
      this.cdr.markForCheck();
    });
  }

  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {

    const elementExists = document.getElementsByClassName('iti__search-input').length;

    if (elementExists) {
      const element =
        this.renderer.selectRootElement('.iti__search-input');
      setTimeout(() => element.focus(), 200);
    }
  }

  private updateCountryVisibility(): void {
    const iti = this.phoneInput.nativeElement.closest('.iti');

    if (!iti) {
      return;
    }

    const shouldShow =
      document.activeElement === this.phoneInput.nativeElement ||
      !!this.phoneNumberControl.value;

    iti.classList.toggle('show-country', shouldShow);
    iti.classList.toggle('hide-country', !shouldShow);
  }

  ngOnDestroy(): void {
    if (this.iti) {
      this.iti.destroy();
    }
  }

  private updateDropdownWidth(): void {
    requestAnimationFrame(() => {
      const dropdown = document.querySelector('.iti__country-selector') as HTMLElement;

      if (dropdown) {
        const width = this.phoneWrapper.nativeElement.getBoundingClientRect().width;
        dropdown.style.width = `${width}px`;
      }
    });
  }
}